import { useEffect, useState } from 'preact/hooks'
import { companiesApi, type CompanyProfileDto } from '@/api/companies'

interface UseCompanyProfileResult {
  profile: CompanyProfileDto | null
  loading: boolean
  error: string | null
  /** The workspace has no record of this company — a 404, which is the tenancy boundary. */
  notFound: boolean
  refresh: () => void
}

/**
 * Loads one company as this workspace knows it (ORB-623).
 *
 * Everything here is about one company, so it all resets when the company id does. Without that,
 * navigating between two profiles leaves the previous company's data on screen under the new URL
 * when the second fetch fails.
 */
export function useCompanyProfile(
  clientCompanyId: string,
  workspaceId: string,
  companyId: string,
): UseCompanyProfileResult {
  const [profile, setProfile] = useState<CompanyProfileDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    setProfile(null)
  }, [companyId])

  useEffect(() => {
    setLoading(true)
    setError(null)
    setNotFound(false)
    const ctrl = new AbortController()
    companiesApi
      .getWorkspaceProfile(clientCompanyId, workspaceId, companyId, ctrl.signal)
      .then(res => {
        if (ctrl.signal.aborted) return
        if (res.data) setProfile(res.data)
        // 404 is the boundary, not a failure: this workspace has never touched this company. Say
        // that, rather than an error the user can do nothing about.
        else if (res.status === 404) setNotFound(true)
        else setError(res.error || 'Could not load this company')
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
    return () => ctrl.abort()
  }, [clientCompanyId, workspaceId, companyId, refreshKey])

  return { profile, loading, error, notFound, refresh: () => setRefreshKey(k => k + 1) }
}
